import { Tooltip, Typography } from "antd";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { InputObject } from "../controls/default";
import { DataLog } from "../repository/data.default";
import { nameOf } from "../utils/typescript";

export interface DataLogsForm {
  readonly id: number;
  readonly tableName: string;
  readonly dateTime: Date;
  readonly oldValues: string;
  readonly newValues: string;
  readonly sourceUid: string;
  readonly sourceType: InputObject;
}

// Audit value columns hold JSON — show the raw string truncated in the cell, with a pretty-printed,
// scrollable tooltip on hover so admins can actually read the before/after change.
const jsonCell = (value?: string) => {
  if (!value) return "—";
  let pretty = value;
  try {
    pretty = JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    /* leave as-is if it isn't valid JSON */
  }
  return (
    <Tooltip
      overlayStyle={{ maxWidth: 520 }}
      title={
        <pre className="m-0 max-h-80 overflow-auto whitespace-pre-wrap text-xs">
          {pretty}
        </pre>
      }
    >
      <Typography.Text className="block truncate">{value}</Typography.Text>
    </Tooltip>
  );
};

export const DataLogsViewColumns: DataColumn[] = [
  {
    title: "Table Name",
    key: nameOf<DataLog>("tableName"),
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Date Time",
    key: nameOf<DataLog>("dateTime"),
    width: 192,
    sort: true,
    format: "DD/MM/YYYY h:mm:ss a",
  },
  {
    title: "Key Values",
    key: nameOf<DataLog>("keyValues"),
    width: 320,
    sort: true,
    transform: jsonCell,
  },
  {
    title: "Old Value",
    key: nameOf<DataLog>("oldValues"),
    width: 320,
    sort: true,
    transform: jsonCell,
  },
  {
    title: "New Value",
    key: nameOf<DataLog>("newValues"),
    width: 320,
    sort: true,
    transform: jsonCell,
  },
  {
    title: "Source Id",
    key: nameOf<DataLog>("sourceUid"),
    width: 192,
    sort: true,
  },
  {
    title: "Source Type",
    key: nameOf<DataLog>("sourceType"),
    width: 192,
    sort: true,
  },
];

const dataLogsQueryMappings: QueryFieldMapping[] = [
  { formField: "tableName", operation: QueryOperation.StartsWith },
  { formField: "oldValues", operation: QueryOperation.Contains },
  { formField: "newValues", operation: QueryOperation.Contains },
  { formField: "sourceUid", operation: QueryOperation.Equal },
  { formField: "sourceType", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "dateTime", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapDataLogsViewQuery = (values: DataLogsForm): SearchQuery =>
  buildSearchQuery(values, dataLogsQueryMappings);

// Source of the change — the ChangeSourceEventType enum (NO_TRACK / User / Service).
const sourceTypeRequest = () =>
  Promise.resolve([
    { display: "No Track", value: "NO_TRACK" },
    { display: "User", value: "User" },
    { display: "Service", value: "Service" },
  ] as InputObject[]);

export const dataLogsSearchSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        { type: "textbox", name: "tableName", label: "Table Name" },
        { type: "rangepicker", name: "dateTime", label: "Date Time" },
        { type: "textbox", name: "oldValues", label: "Old Values" },
        { type: "textbox", name: "newValues", label: "New Values" },
        { type: "textbox", name: "sourceUid", label: "Source Id" },
        { type: "combobox", name: "sourceType", label: "Source Type", request: sourceTypeRequest },
      ],
    },
  ],
};
